/**
 * ProgressBar — reusable linear progress indicator.
 *
 * Matches the visual language of PoseRef panels: thin track, accent-colored
 * fill, optional caption row with label + percent. Designed to be composable
 * so future panels (exporters, bakers, importers, heavy workers) can adopt it
 * without duplicating styles.
 *
 * Defaults are chosen to match the inking bake usage and the broader panel
 * density, but every visual knob is tunable so the component stays generic.
 */

import type { CSSProperties, ReactNode } from "react";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_TEXT_DIM,
  FONT_SIZE_SMALL,
} from "./tokens.ts";

export interface ProgressBarProps {
  /**
   * Current progress. If `undefined`, the bar renders in indeterminate mode
   * with a subtle left-to-right shimmer. Otherwise clamped to `[0, 1]`.
   */
  value?: number;
  /** Left-aligned caption (usually the current phase). */
  label?: ReactNode;
  /** Right-aligned caption. Defaults to a percentage when `value` is set. */
  trailing?: ReactNode;
  /** Suppress the caption row entirely. */
  hideCaption?: boolean;
  /** Track color — defaults to the panel border token. */
  trackColor?: string;
  /** Fill color — defaults to `COLOR_ACCENT`. */
  fillColor?: string;
  /** Track height in px. Defaults to 4. */
  height?: number;
  /** Additional style overrides applied to the outer wrapper. */
  style?: CSSProperties;
  /** Accessible name for screen readers. Falls back to the `label` string. */
  ariaLabel?: string;
  /** Optional `data-testid` passthrough for integration testing. */
  testId?: string;
}

const TRANSITION = "width 120ms linear";
const INDETERMINATE_KEYFRAME_NAME = "pose-progress-indeterminate";

let _keyframesInjected = false;
function ensureIndeterminateKeyframes(): void {
  if (_keyframesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-pose-progress-keyframes", "");
  style.textContent =
    `@keyframes ${INDETERMINATE_KEYFRAME_NAME} {` +
    `0% { transform: translateX(-100%); }` +
    `100% { transform: translateX(400%); }` +
    `}`;
  document.head.appendChild(style);
  _keyframesInjected = true;
}

function defaultTrailing(value: number | undefined): ReactNode {
  if (value === undefined) return null;
  const pct = Math.max(0, Math.min(1, value));
  return `${Math.round(pct * 100)}%`;
}

export function ProgressBar({
  value,
  label,
  trailing,
  hideCaption,
  trackColor = COLOR_BORDER,
  fillColor = COLOR_ACCENT,
  height = 4,
  style,
  ariaLabel,
  testId,
}: ProgressBarProps) {
  const indeterminate = value === undefined;
  const pct = indeterminate ? 0 : Math.max(0, Math.min(1, value));
  const resolvedTrailing = trailing ?? defaultTrailing(value);
  const showCaption =
    !hideCaption && (label !== undefined || resolvedTrailing !== null);

  if (indeterminate) {
    ensureIndeterminateKeyframes();
  }

  // Prefer the explicit ariaLabel; fall back to the label if it's a string.
  const resolvedAriaLabel =
    ariaLabel ?? (typeof label === "string" ? label : undefined);

  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 0,
        ...style,
      }}
    >
      {showCaption && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            minWidth: 0,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          {resolvedTrailing !== null && <span>{resolvedTrailing}</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-label={resolvedAriaLabel}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={indeterminate ? undefined : pct}
        aria-busy={indeterminate || undefined}
        style={{
          position: "relative",
          height,
          borderRadius: Math.max(2, Math.floor(height / 2)),
          background: trackColor,
          overflow: "hidden",
        }}
      >
        {indeterminate ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: "25%",
              background: fillColor,
              animation: `${INDETERMINATE_KEYFRAME_NAME} 1.1s ease-in-out infinite`,
            }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              width: `${pct * 100}%`,
              background: fillColor,
              transition: TRANSITION,
            }}
          />
        )}
      </div>
    </div>
  );
}
